/**
 * Sesiones de caja.
 *
 * A shift is opened with a counted float and closed with a counted total. The system never
 * shows the expected figure before the count is entered — a count taken while looking at the
 * answer measures nothing, which is the whole point of doing it.
 *
 * A shop keeps a float in the drawer overnight for change and takes the day's money out at the
 * corte, and the books follow the money: the corte moves everything over the float to general
 * cash, and the next opening is held against what it left. Between shifts «Caja (gaveta)» holds
 * the float and «Caja dólares» holds nothing, which is what the drawer holds.
 */

import {
  ZERO,
  addMoney,
  centavos,
  esCamion,
  fechaEnNicaragua,
  formatMoney,
  negMoney,
  orderKey,
  subMoney,
  usdToCordobas,
  fxRate,
  type AnyDomainEvent,
  type Centavos,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { ApplyResult, Effect, Reader, SesionCaja } from '../state.js';
import { lugarEnElLog } from './contadas.js';

/**
 * The owner who took money in hand as it left a drawer, if the event names one who can: an
 * active owner. Anybody else is as good as nobody — the money waits to be received — rather than
 * a reason to refuse the corte or the retiro that named them.
 */
export async function receptorValido(read: Reader, id: string | undefined): Promise<string | null> {
  if (!id) return null;
  const u = await read.usuario(id);
  return u?.activo && u.rol === 'dueno' ? u.id : null;
}

/**
 * What should be in the drawer: the float, plus cash taken in, less change and withdrawals —
 * plus what clients paid off their fiado in cash, less cash handed back for returns and what
 * was paid out of the drawer for purchases and expenses.
 */
export function efectivoEsperado(s: SesionCaja): Centavos {
  return centavos(
    s.fondoInicial +
      s.efectivoCordobas -
      s.cambioEntregado -
      s.retiros +
      s.ingresos +
      (s.abonos ?? 0) -
      (s.devoluciones ?? 0) -
      (s.pagado ?? 0) +
      (s.ajusteCordobas ?? 0),
  );
}

/** Who has the drawer now, and so whose the next count is. */
export const responsableDe = (s: SesionCaja): string => s.responsableId ?? s.usuarioId;

/**
 * Paying out of an open drawer (`sesionId` on a purchase, an expense or a supplier payment): in
 * córdobas, from a till that is open, and never more than it should be holding. The money then
 * leaves «Caja (gaveta)» instead of general cash, and the till's corte expects it gone — one
 * step, where it used to take a retiro and then the payment, and forgetting the retiro booked
 * the money out twice.
 */
export async function pagoDeCaja(
  read: Reader,
  sesionId: string,
  formaPago: string,
  monto: Centavos,
  rev: number,
): Promise<{ rechazo: string } | { efecto: Effect }> {
  if (formaPago !== 'efectivo_cordobas') return { rechazo: 'De la gaveta solo sale efectivo en córdobas' };
  const sesion = await read.sesionCaja(sesionId);
  if (!sesion || sesion.estado !== 'abierta') return { rechazo: 'Esa caja no está abierta' };
  const hay = efectivoEsperado(sesion);
  if (monto > hay) return { rechazo: `En la gaveta no hay tanto: se esperan ${formatMoney(hay)}` };
  return {
    efecto: { kind: 'sesion.patch', id: sesionId, rev, patch: { pagado: addMoney(sesion.pagado ?? ZERO, monto) } },
  };
}

/**
 * Undoing a payment made out of a drawer: back into it while the till is open, so its corte
 * stops expecting it gone; into general cash once it has closed, where the drawer's money went.
 */
export async function devolucionDePago(
  read: Reader,
  sesionId: string | null | undefined,
  monto: Centavos,
  rev: number,
): Promise<{ cuenta: 'CAJA_CORDOBAS' | 'CAJA_FUERTE'; efectos: Effect[] }> {
  const sesion = sesionId ? await read.sesionCaja(sesionId) : undefined;
  if (!sesion || sesion.estado !== 'abierta') return { cuenta: 'CAJA_FUERTE', efectos: [] };
  return {
    cuenta: 'CAJA_CORDOBAS',
    efectos: [
      { kind: 'sesion.patch', id: sesion.id, rev, patch: { pagado: subMoney(sesion.pagado ?? ZERO, monto) } },
    ],
  };
}

/**
 * The dollars that should be in the drawer, in USD cents: the dollars it opened with, plus the
 * dollars customers paid in, plus or minus dollars moved in or out. Change is always given in
 * córdobas, so nothing here goes down when a customer is handed their vuelto.
 */
export function dolaresEsperados(s: SesionCaja): Centavos {
  return centavos(
    (s.fondoDolares ?? 0) +
      s.efectivoDolares +
      (s.ingresosDolares ?? 0) -
      (s.retirosDolares ?? 0) +
      (s.ajusteDolares ?? 0),
  );
}

export interface CierreArgs {
  conteoCordobas: number;
  conteoDolares: number;
  /**
   * Córdobas left in the drawer for the next shift: the float. Zero hands everything on, as a
   * truck's settlement does. Never more than was counted, whatever is asked.
   */
  queda: number;
  /** The official rate: what the drawer's dollars are carried at, and what they leave at. */
  tasaOficial: number;
  eventId: string;
  /** ISO timestamp; the date part is used for the journal entry. */
  fecha: string;
  concepto: string;
  rev: number;
  /**
   * An active owner who took the day's money in hand as it left (`receptorValido`). Null: it
   * waits in «Efectivo por recibir» on this till until the owner counts it.
   */
  recibidoPor: string | null;
  /** Whoever did the count. */
  cerradaPor: string;
  /** A first count that was off and was redone, each currency on its own (`caja.cerrada`). */
  primerConteoCordobas?: number;
  primerConteoDolares?: number;
}

export interface CierreResultado {
  effects: Effect[];
  diferenciaCordobas: Centavos;
  diferenciaDolares: Centavos;
}

/**
 * Closes a shift: posts what the count found over or short, and takes the day's money out of the
 * drawer to general cash, leaving the float.
 *
 * Shared with route settlement, which has to close the driver's caja as part of the same act —
 * two implementations of "what should be in the drawer" would eventually disagree, and the one
 * that disagrees quietly is the one that hides a shortfall.
 */
export function cerrarSesion(sesion: SesionCaja, args: CierreArgs): CierreResultado {
  const esperadoC = efectivoEsperado(sesion);
  const conteoC = centavos(args.conteoCordobas);
  const difC = subMoney(conteoC, esperadoC);

  const conteoD = centavos(args.conteoDolares);
  const difD = subMoney(conteoD, dolaresEsperados(sesion));
  const tasa = fxRate(args.tasaOficial);
  const difDEnCordobas = usdToCordobas(difD, tasa);
  const fecha = fechaEnNicaragua(args.fecha);

  // Never more than was counted: a drawer cannot keep money it does not have.
  const queda = centavos(Math.min(args.queda, conteoC));
  const entregado = subMoney(conteoC, queda);

  const effects: Effect[] = [
    {
      kind: 'sesion.patch',
      id: sesion.id,
      rev: args.rev,
      patch: {
        estado: 'cerrada',
        cerradaEn: args.fecha,
        cerradaPor: args.cerradaPor,
        conteoCordobas: conteoC,
        conteoDolares: conteoD,
        diferenciaCordobas: difC,
        diferenciaDolares: difD,
        queda,
        entregado,
        entregadoDolares: conteoD,
        primerConteoCordobas: args.primerConteoCordobas === undefined ? null : centavos(args.primerConteoCordobas),
        primerConteoDolares: args.primerConteoDolares === undefined ? null : centavos(args.primerConteoDolares),
        ...(args.recibidoPor
          ? { recibidoPor: args.recibidoPor, recibidoEn: args.fecha }
          : {
              porRecibir: addMoney(sesion.porRecibir ?? ZERO, entregado),
              porRecibirDolares: addMoney(sesion.porRecibirDolares ?? ZERO, conteoD),
              porRecibirValor: addMoney(
                sesion.porRecibirValor ?? ZERO,
                usdToCordobas(conteoD, fxRate(args.tasaOficial)),
              ),
            }),
      },
    },
  ];

  /**
   * What the count found, each currency against its own drawer account, dollars at the rate they
   * are carried at. Over and short net into one figure: a córdoba missing and a dollar too many
   * are one difference in the drawer, not two.
   */
  const total = centavos(difC + difDEnCordobas);
  if (difC !== 0 || difDEnCordobas !== 0) {
    const lineas: JournalLine[] = [
      difC >= 0 ? debit('CAJA_CORDOBAS', difC, 'Sobrante') : credit('CAJA_CORDOBAS', negMoney(difC)),
      difDEnCordobas >= 0
        ? debit('CAJA_DOLARES', difDEnCordobas, 'Sobrante en dólares')
        : credit('CAJA_DOLARES', negMoney(difDEnCordobas)),
      total >= 0 ? credit('SOBRANTE_CAJA', total) : debit('FALTANTE_CAJA', negMoney(total), 'Faltante'),
    ];
    effects.push({ kind: 'asiento', entry: entry(args.eventId, fecha, args.concepto, lineas) });
  }

  /**
   * The day's money out of the drawer: the córdobas over the float, and every dollar — change is
   * given in córdobas, so none stay. General cash is where the rest of the shop's cash is kept
   * and expenses are paid from; the drawer is left holding the float. With nobody there to take
   * it in hand it goes to «Efectivo por recibir» instead, and becomes general cash once counted.
   *
   * «Caja dólares» gives up exactly what it carries for this shift, which is not always what the
   * dollars are worth at the rate of the day: a sale books its dollars one payment at a time,
   * each rounded to the centavo, and the official rate may have moved since they came in. That
   * gap is a gain or a loss on exchange, not cash, and general cash takes the dollars at what
   * they are worth.
   */
  const dolaresEnLibros = addMoney(sesion.dolaresContables ?? ZERO, difDEnCordobas);
  const dolaresValen = usdToCordobas(conteoD, tasa);
  const cambiaria = subMoney(dolaresValen, dolaresEnLibros);
  if (entregado > 0 || dolaresValen > 0 || dolaresEnLibros !== 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(args.eventId, fecha, args.recibidoPor ? 'Entrega del corte a efectivo general' : 'Entrega del corte, por recibir', [
        debit(args.recibidoPor ? 'CAJA_FUERTE' : 'EFECTIVO_POR_RECIBIR', addMoney(entregado, dolaresValen), 'Lo del día'),
        credit('CAJA_CORDOBAS', entregado),
        dolaresEnLibros >= 0
          ? credit('CAJA_DOLARES', dolaresEnLibros)
          : debit('CAJA_DOLARES', negMoney(dolaresEnLibros)),
        cambiaria >= 0
          ? credit('GANANCIA_CAMBIARIA', cambiaria)
          : debit('PERDIDA_CAMBIARIA', negMoney(cambiaria)),
      ]),
    });
  }

  return { effects, diferenciaCordobas: difC, diferenciaDolares: difD };
}

/**
 * A drawer changing hands (`caja.entregada`), counted blind by whoever takes it over.
 *
 * Two people on one till used to share one corte, under the name of whoever opened: a shortfall
 * belonged to nobody. Now the count at the handover closes the books on the first person's part —
 * what it finds over or short posts like a corte's, against the till — and the drawer goes on
 * from what was counted, expected at exactly that, in the name of whoever counted. Nothing leaves
 * the drawer: the money stays where it is, and only whose it is changes.
 */
async function entregarCaja(
  event: Extract<AnyDomainEvent, { type: 'caja.entregada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const sesion = await read.sesionCaja(p.sesionId);
  if (!sesion || sesion.estado !== 'abierta') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa caja no está abierta' }] };
  }
  const deQuien = responsableDe(sesion);
  if (deQuien === event.actorId) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa caja ya está a su nombre' }] };
  }
  if (esCamion(sesion.ubicacion)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja de una ruta se entrega al liquidar' }] };
  }

  const difC = subMoney(centavos(p.conteoCordobas), efectivoEsperado(sesion));
  const difD = subMoney(centavos(p.conteoDolares), dolaresEsperados(sesion));
  const difDEnCordobas = usdToCordobas(difD, fxRate((await read.config()).fxTasaOficial));
  const total = centavos(difC + difDEnCordobas);

  const effects: Effect[] = [
    {
      kind: 'sesion.patch',
      id: sesion.id,
      rev,
      patch: {
        responsableId: event.actorId,
        entregadaRev: lugarEnElLog(event),
        entregadaEn: event.clientTs,
        ajusteCordobas: addMoney(sesion.ajusteCordobas ?? ZERO, difC),
        ajusteDolares: addMoney(sesion.ajusteDolares ?? ZERO, difD),
        // The dollars found or missing move what the drawer's dollars are carried at, as at a corte.
        dolaresContables: addMoney(sesion.dolaresContables ?? ZERO, difDEnCordobas),
        diferenciaEntregas: addMoney(sesion.diferenciaEntregas ?? ZERO, total),
        // A count redone: the first one, for the owner to see beside it. One that stood leaves
        // an earlier handover's in place.
        ...(p.primerConteoCordobas !== undefined ? { primerConteoEntregaCordobas: centavos(p.primerConteoCordobas) } : {}),
        ...(p.primerConteoDolares !== undefined ? { primerConteoEntregaDolares: centavos(p.primerConteoDolares) } : {}),
      },
    },
  ];
  if (difC !== 0 || difDEnCordobas !== 0) {
    const quien = (await read.usuario(deQuien))?.nombre ?? '';
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        fechaEnNicaragua(event.clientTs),
        `Entrega de caja${quien ? ` de ${quien}` : ''}${p.nota ? `: ${p.nota}` : ''}`,
        [
          difC >= 0 ? debit('CAJA_CORDOBAS', difC, 'Sobrante') : credit('CAJA_CORDOBAS', negMoney(difC)),
          difDEnCordobas >= 0
            ? debit('CAJA_DOLARES', difDEnCordobas, 'Sobrante en dólares')
            : credit('CAJA_DOLARES', negMoney(difDEnCordobas)),
          total >= 0 ? credit('SOBRANTE_CAJA', total) : debit('FALTANTE_CAJA', negMoney(total), 'Faltante'),
        ],
      ),
    });
  }
  return { effects, rejections: [] };
}

export async function reduceCaja(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'caja.abierta': {
      const p = event.payload;

      if (await read.sesionCaja(p.sesionId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa caja ya fue abierta' }] };
      }

      const abierta = await read.sesionAbiertaDe(event.deviceId);
      if (abierta) {
        return {
          effects: [],
          rejections: [
            { eventId: event.id, reason: 'Ya hay una caja abierta en este dispositivo. Ciérrela primero.' },
          ],
        };
      }

      /**
       * The float, on the books (see the event). What the last corte left is already in the
       * drawer's account; córdobas put in from general cash come across, and so do any dollars
       * counted, since the corte takes every dollar out. With nothing handed on, the whole count
       * is a float brought from general cash. The count is held against all that: money gone
       * overnight comes out short, instead of quietly becoming the new starting point.
       */
      const fondo = centavos(p.fondoInicial);
      const deGeneral = centavos(p.deGeneral ?? (p.esperado === undefined ? p.fondoInicial : 0));
      /*
       * What the corte it takes over left, as the books have it now (2026-10-15): a phone that had
       * not heard yet of the owner correcting that corte would hold the count against what was left
       * before, and the drawer's account would part from the drawer by the correction.
       */
      const relevo = p.relevoDe && p.esperado !== undefined ? await read.sesionCaja(p.relevoDe) : undefined;
      const dejado = relevo?.estado === 'cerrada' ? (relevo.queda ?? 0) : (p.esperado ?? 0);
      const esperado = addMoney(centavos(dejado), deGeneral);
      const diferencia = subMoney(fondo, esperado);
      const dolares = centavos(p.fondoDolares ?? 0);
      const dolaresEnCordobas =
        dolares > 0 ? usdToCordobas(dolares, fxRate((await read.config()).fxTasaOficial)) : ZERO;

      const sesion: SesionCaja = {
        id: p.sesionId,
        usuarioId: event.actorId,
        dispositivoId: event.deviceId,
        ubicacion: p.ubicacion,
        abiertaEn: event.clientTs,
        cerradaEn: null,
        fondoInicial: fondo,
        fondoDolares: dolares,
        estado: 'abierta',
        efectivoCordobas: ZERO,
        efectivoDolares: ZERO,
        otrosPagos: ZERO,
        cambioEntregado: ZERO,
        retiros: ZERO,
        ingresos: ZERO,
        ventas: 0,
        totalVendido: ZERO,
        costoVendido: ZERO,
        fiado: ZERO,
        abonos: ZERO,
        devoluciones: ZERO,
        retirosDolares: ZERO,
        ingresosDolares: ZERO,
        conteoCordobas: null,
        conteoDolares: null,
        diferenciaCordobas: null,
        diferenciaDolares: null,
        relevoDe: p.relevoDe ?? null,
        deGeneral,
        esperadoApertura: esperado,
        diferenciaApertura: diferencia,
        primerConteoApertura: p.primerConteo === undefined ? null : centavos(p.primerConteo),
        dolaresContables: dolaresEnCordobas,
        queda: null,
        entregado: null,
        entregadoDolares: null,
        rev,
      };

      const effects: Effect[] = [{ kind: 'sesion.upsert', id: p.sesionId, row: sesion }];
      const fecha = fechaEnNicaragua(event.clientTs);
      if (deGeneral > 0 || dolaresEnCordobas > 0) {
        effects.push({
          kind: 'asiento',
          entry: entry(event.id, fecha, 'Fondo de caja desde efectivo general', [
            debit('CAJA_CORDOBAS', deGeneral, 'Fondo de caja'),
            debit('CAJA_DOLARES', dolaresEnCordobas, 'Dólares en la gaveta'),
            credit('CAJA_FUERTE', addMoney(deGeneral, dolaresEnCordobas)),
          ]),
        });
      }
      if (diferencia !== 0) {
        const monto = centavos(Math.abs(diferencia));
        effects.push({
          kind: 'asiento',
          entry: entry(
            event.id,
            fecha,
            'Diferencia al abrir la caja',
            diferencia > 0
              ? [debit('CAJA_CORDOBAS', monto, 'Sobrante al abrir'), credit('SOBRANTE_CAJA', monto)]
              : [debit('FALTANTE_CAJA', monto, 'Faltante al abrir'), credit('CAJA_CORDOBAS', monto)],
          ),
        });
      }
      return { effects, rejections: [] };
    }

    case 'caja.movimiento': {
      const p = event.payload;
      const sesion = await read.sesionCaja(p.sesionId);
      if (!sesion) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja no existe' }] };
      }
      if (sesion.estado === 'cerrada') {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja ya fue cerrada' }] };
      }

      const monto = centavos(p.monto);
      const esRetiro = p.tipo === 'retiro';
      /*
       * A retiro goes to general cash only in an owner's hands; otherwise it waits on this till
       * in «Efectivo por recibir», and whatever is short when it is counted is the till's.
       */
      const recibe = esRetiro ? await receptorValido(read, p.recibidoPor) : null;
      const destino = recibe ? 'CAJA_FUERTE' : 'EFECTIVO_POR_RECIBIR';
      const recibido = recibe ? { recibidoPor: recibe, recibidoEn: event.clientTs } : {};

      if (p.moneda === 'USD') {
        /**
         * Dollars go to general cash as dollars, at the official rate they were booked in at.
         * What the shift carries in «Caja dólares» moves by the same amount, so the corte can hand
         * over exactly what is left of it.
         */
        const config = await read.config();
        const enCordobas = usdToCordobas(monto, fxRate(config.fxTasaOficial));
        const concepto = `${esRetiro ? 'Retiro' : 'Ingreso'} de caja, US$ ${formatMoney(monto, { symbol: false })}: ${p.motivo}`;
        return {
          effects: [
            {
              kind: 'sesion.patch',
              id: p.sesionId,
              rev,
              patch: esRetiro
                ? {
                    retirosDolares: addMoney(sesion.retirosDolares ?? ZERO, monto),
                    dolaresContables: subMoney(sesion.dolaresContables ?? ZERO, enCordobas),
                    ...(recibe
                      ? recibido
                      : {
                          porRecibirDolares: addMoney(sesion.porRecibirDolares ?? ZERO, monto),
                          porRecibirValor: addMoney(sesion.porRecibirValor ?? ZERO, enCordobas),
                        }),
                  }
                : {
                    ingresosDolares: addMoney(sesion.ingresosDolares ?? ZERO, monto),
                    dolaresContables: addMoney(sesion.dolaresContables ?? ZERO, enCordobas),
                  },
            },
            {
              kind: 'asiento',
              entry: entry(
                event.id,
                fechaEnNicaragua(event.clientTs),
                concepto,
                esRetiro
                  ? [debit(destino, enCordobas, p.motivo), credit('CAJA_DOLARES', enCordobas)]
                  : [debit('CAJA_DOLARES', enCordobas, p.motivo), credit('CAJA_FUERTE', enCordobas)],
              ),
            },
          ],
          rejections: [],
        };
      }

      return {
        effects: [
          {
            kind: 'sesion.patch',
            id: p.sesionId,
            rev,
            patch: esRetiro
              ? {
                  retiros: addMoney(sesion.retiros, monto),
                  ...(recibe ? recibido : { porRecibir: addMoney(sesion.porRecibir ?? ZERO, monto) }),
                }
              : { ingresos: addMoney(sesion.ingresos, monto) },
          },
          {
            /**
             * Money moving between two places the business owns, not income or expense. It
             * leaves the drawer so the expected count drops, and lands in the safe so the
             * balance sheet still shows it. Reclassifying it as an owner draw is a separate,
             * deliberate decision.
             */
            kind: 'asiento',
            entry: entry(
              event.id,
              fechaEnNicaragua(event.clientTs),
              `${esRetiro ? 'Retiro' : 'Ingreso'} de caja: ${p.motivo}`,
              esRetiro
                ? [debit(destino, monto, p.motivo), credit('CAJA_CORDOBAS', monto)]
                : [debit('CAJA_CORDOBAS', monto, p.motivo), credit('CAJA_FUERTE', monto)],
            ),
          },
        ],
        rejections: [],
      };
    }

    case 'caja.cerrada': {
      const p = event.payload;
      const sesion = await read.sesionCaja(p.sesionId);
      if (!sesion) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja no existe' }] };
      }
      if (sesion.estado === 'cerrada') {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja ya fue cerrada' }] };
      }
      /**
       * A route's till closes with its settlement, which squares the cash and the goods in one act.
       * Closed on its own, the settlement could never happen: the truck's stock would never be
       * counted back and its shortfall never booked.
       */
      if (esCamion(sesion.ubicacion)) {
        const jornada = await read.jornadaAbiertaDe(sesion.ubicacion);
        if (jornada && jornada.sesionId === sesion.id) {
          return {
            effects: [],
            rejections: [{ eventId: event.id, reason: 'Esta caja es de una ruta abierta: se cierra al liquidar' }],
          };
        }
      }

      const { effects } = cerrarSesion(sesion, {
        conteoCordobas: p.conteoCordobas,
        conteoDolares: p.conteoDolares,
        queda: p.queda,
        tasaOficial: (await read.config()).fxTasaOficial,
        eventId: event.id,
        fecha: event.clientTs,
        concepto: `Corte de caja${p.notas ? `: ${p.notas}` : ''}`,
        rev,
        recibidoPor: await receptorValido(read, p.recibidoPor),
        cerradaPor: event.actorId,
        ...(p.primerConteoCordobas !== undefined ? { primerConteoCordobas: p.primerConteoCordobas } : {}),
        ...(p.primerConteoDolares !== undefined ? { primerConteoDolares: p.primerConteoDolares } : {}),
      });

      return { effects, rejections: [] };
    }

    case 'caja.entregada':
      return entregarCaja(event, read, rev);

    default:
      return null;
  }
}
